import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Download, FileSpreadsheet, FileText } from 'lucide-react';
/**
 * Surfaces the latest version of a gov-provided source template across
 * amendments. Used by both the workbook workspace and the template
 * workspace.
 *
 * The amendment-aware lookup happens server-side in
 * resolveVolumeSourceRefs — by the time this component renders,
 * `attachment` already points at the most recent version.
 *
 * Download URL is currently best-effort: we link to the existing files
 * download endpoint by attachment id. Auth is handled by the existing
 * file-access flow.
 */ export function SourceTemplateCard({ attachment, kind = 'template' }) {
    const Icon = kind === 'workbook' ? FileSpreadsheet : FileText;
    const sizeLabel = formatBytes(Number(attachment.size ?? 0));
    const downloadHref = `/api/files/${encodeURIComponent(attachment.attachmentId)}/download`;
    return /*#__PURE__*/ _jsx("div", {
        className: "rounded-xl border border-slate-200 bg-white p-4 shadow-sm",
        children: /*#__PURE__*/ _jsxs("div", {
            className: "flex items-start gap-3",
            children: [
                /*#__PURE__*/ _jsx("div", {
                    className: "flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg bg-slate-100 text-slate-600",
                    children: /*#__PURE__*/ _jsx(Icon, {
                        className: "h-5 w-5",
                        "aria-hidden": "true"
                    })
                }),
                /*#__PURE__*/ _jsxs("div", {
                    className: "min-w-0 flex-1",
                    children: [
                        /*#__PURE__*/ _jsxs("div", {
                            className: "flex items-center gap-2",
                            children: [
                                /*#__PURE__*/ _jsx("span", {
                                    className: "text-[11px] font-medium uppercase tracking-wide text-slate-500",
                                    children: "Source template"
                                }),
                                attachment.amendmentNumber && /*#__PURE__*/ _jsxs("span", {
                                    className: "rounded-full border border-amber-200 bg-amber-50 px-1.5 py-px text-[10px] font-medium text-amber-800",
                                    children: [
                                        "Amendment ",
                                        attachment.amendmentNumber
                                    ]
                                }),
                                !attachment.amendmentNumber && /*#__PURE__*/ _jsx("span", {
                                    className: "rounded-full border border-slate-200 bg-slate-50 px-1.5 py-px text-[10px] font-medium text-slate-600",
                                    children: "Base"
                                })
                            ]
                        }),
                        /*#__PURE__*/ _jsx("div", {
                            className: "mt-1 truncate text-sm font-medium text-slate-900",
                            children: attachment.filename
                        }),
                        sizeLabel && /*#__PURE__*/ _jsx("div", {
                            className: "mt-0.5 text-[11px] text-slate-500",
                            children: sizeLabel
                        })
                    ]
                }),
                /*#__PURE__*/ _jsxs("a", {
                    href: downloadHref,
                    download: attachment.filename,
                    target: "_blank",
                    rel: "noreferrer",
                    className: "inline-flex flex-shrink-0 items-center gap-1.5 rounded-md border border-slate-200 bg-white px-3 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-50",
                    children: [
                        /*#__PURE__*/ _jsx(Download, {
                            className: "h-3.5 w-3.5",
                            "aria-hidden": "true"
                        }),
                        "Download"
                    ]
                })
            ]
        })
    });
}
function formatBytes(bytes) {
    if (!bytes || Number.isNaN(bytes)) return '';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
    return `${(bytes / (1024 * 1024 * 1024)).toFixed(1)} GB`;
}
